import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Page-level error boundary for the admin SPA.
 *
 * Why this exists: the admin had NO error boundary, so a single component
 * throw blanked the ENTIRE app (incident 2026-05-29: a StatusBadge crash on
 * a NULL user status white-screened /users). Wrapping the routed <Outlet>
 * means any render fault now degrades to a localized, recoverable panel —
 * the sidebar/header stay intact and the operator can navigate away.
 *
 * Mount it with `key={location.pathname}` so navigating to a new route
 * remounts it and clears a stuck error automatically.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface to the console for diagnosis; the boundary keeps the app alive.
    // eslint-disable-next-line no-console
    console.error('[admin] uncaught render error:', error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        className="flex h-full min-h-[60vh] flex-col items-center justify-center gap-3 p-8 text-center"
      >
        <h2 className="text-lg font-semibold text-foreground">Something went wrong on this page</h2>
        <p className="max-w-md text-sm text-muted-foreground">
          A component failed to render. The rest of the admin is unaffected — try again, or use the
          sidebar to navigate elsewhere.
        </p>
        <pre className="max-w-full overflow-auto rounded bg-muted p-3 text-left text-xs text-destructive">
          {error.message}
        </pre>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={this.reset}
            className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded border bg-background px-3 py-1.5 text-sm font-medium hover:bg-muted"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
